import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Build as MaintenanceIcon } from '@mui/icons-material';
import {
  computeMinutesUntilEnd,
  formatDateTime,
  formatMaintenanceCountdown,
} from '../utils/maintenanceBlock';

const MaintenanceMessage = ({
  open,
  maintenance,
}) => {
  const [minutesRemaining, setMinutesRemaining] = useState(null);

  useEffect(() => {
    if (!open || !maintenance) {
      setMinutesRemaining(null);
      return undefined;
    }

    const updateCountdown = () => {
      setMinutesRemaining(computeMinutesUntilEnd(maintenance));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 30000);
    return () => window.clearInterval(intervalId);
  }, [open, maintenance]);

  const countdownLabel = useMemo(
    () => formatMaintenanceCountdown(minutesRemaining),
    [minutesRemaining]
  );

  if (!maintenance) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '2px solid',
          borderColor: 'warning.main',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <MaintenanceIcon sx={{ color: 'warning.main' }} />
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          Scheduled Maintenance
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          {maintenance.title || 'Server Maintenance in Progress'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
          {maintenance.description || "Sorry, the servers are under maintenance, we'll be back soon!"}
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'warning.light',
            color: 'warning.contrastText',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Estimated time remaining
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {countdownLabel || 'Ending soon'}
          </Typography>
          {maintenance.end_utc ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
              Expected end: {formatDateTime(maintenance.end_utc)}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceMessage;
